import {
  ApiError,
  CaseScheduleApiClient,
  caseScheduleApiClient,
} from '@peritasai/data-access';
import {
  AssetLifecycleFunction,
  CartCompletenessStatus,
  CaseBuildStatus,
  CaseEditingSessionDto,
  CasesResponseDTO,
  PreferenceCardResponseDto,
  UntrackedAssetType,
  UpdateSuppliesAndInstrumentsDto,
  UpdateSuppliesAndInstrumentsItemDto,
  WorkflowSummaryResponseDTO,
} from '@peritasai/types';
import { BuildCartPhase } from '../build-cart';
import { ChecklistItemEntity } from '../build-cart/checklist-item.entity';
import { CaseEditingSessionEntity } from './case-editing-session.entity';
import { SurgicalCaseEntity } from './case-schedule.entity';
import { PickingStationEntity } from './picking-station.entity';
import {
  WorkflowCaseItemEntity,
  WorkflowSummaryResponseEntity,
} from './workflow-summary.entities';

export interface ICaseScheduleRepository {
  getCases(options: { page: number; pageSize: number }): Promise<CasesResponse>;

  getTodayCasesForLocation(locationId: string): Promise<SurgicalCaseEntity[]>;

  getCase(
    caseId: string,
    options?: { includeChecklistItems?: boolean }
  ): Promise<SurgicalCaseEntity>;

  addTrackedItem(
    caseId: string,
    checkListItem: ChecklistItemEntity,
    isCorrectCart?: boolean
  ): Promise<SurgicalCaseEntity>;

  assignTrackedItemsBatch(
    caseId: string,
    assetIds: string[],
    firstCartAssetId?: string
  ): Promise<SurgicalCaseEntity>;

  confirmUnexpectedItem(
    caseId: string,
    checkListItem: ChecklistItemEntity
  ): Promise<SurgicalCaseEntity>;

  reserveTrackedItemsBatch(
    caseId: string,
    trayAssetIds: string[],
    firstCartAssetId?: string
  ): Promise<SurgicalCaseEntity>;

  unassignTrackedItem(
    caseId: string,
    assetId: string
  ): Promise<SurgicalCaseEntity>;

  updateBuildStatus(
    caseId: string,
    buildStatus: CaseBuildStatus
  ): Promise<SurgicalCaseEntity>;

  updateDispatchStatus(
    caseId: string,
    dispatchStatus: boolean
  ): Promise<void>;

  verifyAssets(caseId: string, verifiedBy: string): Promise<SurgicalCaseEntity>;

  startEditingSession(caseId: string): Promise<CaseEditingSessionEntity>;
  extendEditingSession(caseId: string): Promise<CaseEditingSessionEntity>;
  endEditingSession(caseId: string): Promise<void>;
  getEditingSession(caseId: string): Promise<CaseEditingSessionEntity | null>;

  getWorkflowSummary(
    targetDate: string
  ): Promise<WorkflowSummaryResponseEntity>;

  getPickingStations(): Promise<PickingStationEntity[]>;

  updateSidehubAvailableStatus(
    sidehubId: string,
    available: boolean
  ): Promise<void>;
}

export interface CasesResponse extends Omit<CasesResponseDTO, 'cases'> {
  cases: SurgicalCaseEntity[];
}

export class CaseScheduleRepository implements ICaseScheduleRepository {
  constructor(private readonly apiClient: CaseScheduleApiClient) {}

  async getCase(
    caseId: string,
    options?: { includeChecklistItems?: boolean }
  ): Promise<SurgicalCaseEntity> {
    const dto = await this.apiClient.getCase(caseId, options);
    let preferenceCard: PreferenceCardResponseDto | undefined;

    const preferenceCardId = dto.buildChecklist?.preferenceCardId;

    if (preferenceCardId) {
      try {
        preferenceCard = await this.apiClient.getPreferenceCard(
          preferenceCardId
        );
      } catch (error) {
        console.error('Failed to load preference card', error);
      }
    }

    return new SurgicalCaseEntity(dto, preferenceCard);
  }

  async addTrackedItem(
    caseId: string,
    checkListItem: ChecklistItemEntity,
    isCorrectCart = true,
    assignmentType: BuildCartPhase = BuildCartPhase.TRAY
  ): Promise<SurgicalCaseEntity> {
    // Handle wrong cart detection simulation
    if (!isCorrectCart) {
      const error: any = new Error('Wrong cart detected');
      error.errorType = 'wrong-cart-detected';
      error.wrongCartName = 'CC-WRONG-CART';
      throw error;
    }

    const assetId = checkListItem.assetId;
    if (!assetId) {
      const error: any = new Error('Asset ID is required for assignment');
      error.code = 'asset-id-required';
      throw error;
    }

    const dto =
      assignmentType === BuildCartPhase.CART
        ? await this.apiClient.updateTrackedItems(caseId, { assetId })
        : await this.apiClient.updateTrackedItemsBatch(caseId, {
            assetIds: [assetId],
          });

    const preferenceCardId = dto.buildChecklist?.preferenceCardId;
    let preferenceCard: PreferenceCardResponseDto | undefined;

    if (preferenceCardId) {
      try {
        preferenceCard = await this.apiClient.getPreferenceCard(
          preferenceCardId
        );
      } catch (error) {
        console.error('Failed to load preference card', error);
      }
    }

    return new SurgicalCaseEntity(dto, preferenceCard);
  }

  async assignTrackedItemsBatch(
    caseId: string,
    assetIds: string[],
    firstCartAssetId?: string
  ): Promise<SurgicalCaseEntity> {
    const dto = await this.apiClient.updateTrackedItemsBatch(caseId, {
      assetIds,
      firstCartAssetId,
    });
    return new SurgicalCaseEntity(dto);
  }

  async confirmUnexpectedItem(
    caseId: string,
    checkListItem: ChecklistItemEntity
  ): Promise<SurgicalCaseEntity> {
    const dto = await this.apiClient.updateUnexpectedItem(
      caseId,
      checkListItem.checklistItemId
    );
    return new SurgicalCaseEntity(dto);
  }

  async reserveTrackedItemsBatch(
    caseId: string,
    trayAssetIds: string[],
    firstCartAssetId?: string
  ): Promise<SurgicalCaseEntity> {
    const dto = await this.apiClient.reserveTrackedItemsBatch(caseId, {
      trayAssetIds,
      firstCartAssetId,
    });
    return new SurgicalCaseEntity(dto);
  }

  async unassignTrackedItem(
    caseId: string,
    assetId: string
  ): Promise<SurgicalCaseEntity> {
    const dto = await this.apiClient.unassignTrackedItem(caseId, { assetId });
    return new SurgicalCaseEntity(dto);
  }

  async updateBuildStatus(
    caseId: string,
    buildStatus: CaseBuildStatus
  ): Promise<SurgicalCaseEntity> {
    const dto = await this.apiClient.updateBuildStatus(caseId, buildStatus);

    const preferenceCardId = dto.buildChecklist?.preferenceCardId;
    let preferenceCard: PreferenceCardResponseDto | undefined;

    if (preferenceCardId) {
      try {
        preferenceCard = await this.apiClient.getPreferenceCard(
          preferenceCardId
        );
      } catch (error) {
        console.error('Failed to load preference card', error);
      }
    }

    return new SurgicalCaseEntity(dto, preferenceCard);
  }

  async updateDispatchStatus(
    caseId: string,
    dispatchStatus: boolean
  ): Promise<void> {
    await this.apiClient.updateDispatchStatus(caseId, dispatchStatus);
  }

  async verifyAssets(
    caseId: string,
    verifiedBy: string
  ): Promise<SurgicalCaseEntity> {
    const dto = await this.apiClient.verifyCaseAssets(caseId, verifiedBy);
    return new SurgicalCaseEntity(dto);
  }

  async startEditingSession(caseId: string): Promise<CaseEditingSessionEntity> {
    const dto = await this.apiClient.startEditingSession(caseId);
    return new CaseEditingSessionEntity(dto);
  }

  async extendEditingSession(
    caseId: string
  ): Promise<CaseEditingSessionEntity> {
    const dto = await this.apiClient.extendEditingSession(caseId);
    return new CaseEditingSessionEntity(dto);
  }

  async endEditingSession(caseId: string): Promise<void> {
    await this.apiClient.endEditingSession(caseId);
  }

  async getEditingSession(
    caseId: string
  ): Promise<CaseEditingSessionEntity | null> {
    const statusDto = await this.apiClient.getEditingSessionStatus(caseId);
    if (!statusDto || !statusDto.isActive) {
      return null;
    }

    // If status shows locked by another user, throw an error with details
    // This allows the caller to know who has it locked
    if (statusDto.lockedBy && statusDto.lockedBy.userId) {
      throw new ApiError(
        { status: 409, statusText: 'Conflict' },
        {
          message: `Session locked by another user: ${
            statusDto.lockedBy.userInitials || statusDto.lockedBy.userId
          }`,
          lockedBy: {
            userId: statusDto.lockedBy.userId,
            userInitials: statusDto.lockedBy.userInitials,
          },
        }
      );
    }

    // If we have caseId and userId, we can create a partial entity
    // But we don't have startedAt, lastActivity, expiresAt from status
    // So we need to convert timeRemaining to expiresAt
    if (
      statusDto.caseId &&
      statusDto.userId &&
      statusDto.timeRemaining !== undefined
    ) {
      const now = new Date();
      const expiresAt = new Date(
        now.getTime() + statusDto.timeRemaining * 1000
      );

      // Create a DTO-like object with estimated timestamps
      const estimatedDto: CaseEditingSessionDto = {
        caseId: statusDto.caseId,
        userId: statusDto.userId,
        userInitials: statusDto.userInitials,
        startedAt: new Date(
          now.getTime() - statusDto.timeRemaining * 1000
        ).toISOString(), // Estimate
        lastActivity: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
      };

      return new CaseEditingSessionEntity(estimatedDto);
    }

    return null;
  }

  async updateUntrackedItem(
    caseId: string,
    checkListItem: ChecklistItemEntity,
    untrackedAssetType: UntrackedAssetType
  ): Promise<SurgicalCaseEntity> {
    const itemDto: UpdateSuppliesAndInstrumentsItemDto = {
      catalogItemId: checkListItem.catalogItemId,
      assetId: undefined,
      allocated: !checkListItem.allocated,
      notes: checkListItem.notes || undefined,
    };

    const requestDto: UpdateSuppliesAndInstrumentsDto = {
      supplies:
        untrackedAssetType === UntrackedAssetType.SUPPLY ? [itemDto] : [],
      singlePackedInstruments:
        untrackedAssetType === UntrackedAssetType.SINGLE_PACKED_INSTRUMENT
          ? [itemDto]
          : [],
      ...(untrackedAssetType === UntrackedAssetType.INSTRUMENT && {
        instruments: [itemDto],
      }),
    };

    const dto = await this.apiClient.updateSuppliesAndInstruments(
      caseId,
      requestDto
    );

    // Merge the updated checklist into the case DTO
    const updatedDto = {
      ...dto,
    };

    const preferenceCardId = updatedDto.buildChecklist?.preferenceCardId;
    let preferenceCard: PreferenceCardResponseDto | undefined;

    if (preferenceCardId) {
      try {
        preferenceCard = await this.apiClient.getPreferenceCard(
          preferenceCardId
        );
      } catch (error) {
        console.error('Failed to load preference card', error);
      }
    }

    return new SurgicalCaseEntity(updatedDto, preferenceCard);
  }

  async getCases(options: {
    page: number;
    pageSize: number;
    startDate: string;
    endDate?: string;
    locationId?: string;
    includeChecklistItems?: true;
  }): Promise<CasesResponse> {
    const dtoResponse = await this.apiClient.getCases(options);

    const cases = dtoResponse.cases.map((dto) => new SurgicalCaseEntity(dto));

    return {
      ...dtoResponse,
      cases,
    };
  }

  async getTodayCasesForLocation(
    locationId: string
  ): Promise<SurgicalCaseEntity[]> {
    const { startDate, endDate } = CaseScheduleRepository.getTodayRangeISO();

    const response = await this.getCases({
      page: 1,
      pageSize: 100,
      startDate,
      endDate,
      locationId,
    });

    return response.cases;
  }

  /**
   * Today's local calendar day as ISO start/end-of-day boundaries
   */
  private static getTodayRangeISO(): { startDate: string; endDate: string } {
    const now = new Date();
    const today = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-');

    return {
      startDate: CaseScheduleRepository.convertDateToStartOfDayISO(today),
      endDate: CaseScheduleRepository.convertDateToEndOfDayISO(today),
    };
  }

  async getWorkflowSummary(
    targetDate: string
  ): Promise<WorkflowSummaryResponseEntity> {
    // Build summary from cases (fallback implementation)
    const pageSize = 50;
    // Convert date-only string (YYYY-MM-DD) to full datetime ISO strings
    // with proper day boundaries to avoid timezone issues
    const startDate =
      CaseScheduleRepository.convertDateToStartOfDayISO(targetDate);
    const endDate = CaseScheduleRepository.convertDateToEndOfDayISO(targetDate);

    const dayCasesResponse = await this.getCases({
      page: 1,
      pageSize: pageSize,
      startDate: startDate,
      endDate: endDate,
      includeChecklistItems: true,
    });
    const dayCases = dayCasesResponse.cases;

    // Calculate completion percentage
    const completedCases = dayCases.filter(
      (case_) =>
        case_.cartCompletenessStatus === 'Dispatched' ||
        case_.cartCompletenessStatus === 'Dispatched - Incomplete'
    ).length;
    const completionPercentage =
      dayCases.length > 0
        ? Math.round((completedCases / dayCases.length) * 100)
        : 0;

    // Generate summaries for each category
    const actionableCases = this.getActionableCases(dayCases);
    const blockedCases = this.getBlockedCases(dayCases);
    const inProgressCases = this.getInProgressSummary(dayCases);
    const dispatchedCases = this.getDispatchedSummary(dayCases);

    // Build summary DTO and convert to entity
    const summaryDTO = {
      date: targetDate,
      totalCasesDue: dayCases.length,
      completionPercentage,
      actionableCases: actionableCases.map((item: WorkflowCaseItemEntity) =>
        item.toDTO()
      ),
      blockedCases: blockedCases.map((item: WorkflowCaseItemEntity) =>
        item.toDTO()
      ),
      inProgressCases,
      dispatchedCases,
    };

    const responseDTO: WorkflowSummaryResponseDTO = {
      summary: summaryDTO,
      dateOptions: [],
    };

    return new WorkflowSummaryResponseEntity(responseDTO);
  }

  async getPickingStations(): Promise<PickingStationEntity[]> {
    const sidehubs = await this.apiClient.getSideHubs();

    // A sidehub's own `function` (dispatch/checkIn/operatingRoom/rack) describes
    // the hub hardware itself, not the location it sits in — the location's
    // lifecycle function lives on `locationDetails.assetLifecycleFunction`.
    const pickingSidehubs = sidehubs.filter(
      (sidehub) =>
        sidehub.locationDetails?.assetLifecycleFunction ===
        AssetLifecycleFunction.Picking
    );

    // Map sidehubs to PickingStationEntity
    return pickingSidehubs.map((sidehub) => {
      // Map status from three booleans to status string
      let status: 'Available' | 'Busy' | 'Offline';
      if (!sidehub.enabled || !sidehub.active) {
        status = 'Offline';
      } else if (sidehub.enabled && sidehub.active && !sidehub.available) {
        status = 'Busy';
      } else {
        // enabled && active && available
        status = 'Available';
      }

      const locationDisplayName =
        sidehub.locationDetails?.locationDisplayName ?? undefined;

      return new PickingStationEntity({
        id: sidehub.id,
        name: locationDisplayName ?? sidehub.deviceId,
        status,
        locationDisplayName,
        assetLifecycleFunction:
          sidehub.locationDetails?.assetLifecycleFunction ?? undefined,
        locationId: sidehub.location?.id ?? sidehub.locationId ?? null,
      });
    });
  }

  async updateSidehubAvailableStatus(
    sidehubId: string,
    available: boolean
  ): Promise<void> {
    await this.apiClient.updateSidehubAvailableStatus(sidehubId, available);
  }

  destroy(): void {
    // No-op for real repository
  }

  private getActionableCases(
    dayCases: SurgicalCaseEntity[]
  ): WorkflowCaseItemEntity[] {
    const actionableFiltered = dayCases.filter(
      (case_) =>
        case_.cartCompletenessStatus === 'Ready for Dispatch' ||
        case_.cartCompletenessStatus === 'Incomplete' ||
        case_.cartCompletenessStatus === 'Not Started'
    );

    return actionableFiltered.map((case_) => {
      const timeUntilSurgery = CaseScheduleRepository.calculateTimeUntilSurgery(
        case_.dateTime
      );
      const isPriority = timeUntilSurgery.includes('59mins'); // A bit of a hack, but matches original logic

      return new WorkflowCaseItemEntity({
        id: case_.id,
        caseHospitalId: case_.caseHospitalId || case_.id, // Fallback to ID if hospital ID is missing
        surgeryTime: CaseScheduleRepository.formatSurgeryTime(case_.dateTime),
        dueTime: timeUntilSurgery,
        action:
          case_.cartCompletenessStatus === 'Ready for Dispatch'
            ? 'Dispatch'
            : 'Build',
        type: CaseScheduleRepository.getCaseType(
          case_.cartCompletenessStatus as CartCompletenessStatus
        ),
        isPriority,
      });
    });
  }

  private getBlockedCases(
    dayCases: SurgicalCaseEntity[]
  ): WorkflowCaseItemEntity[] {
    const blockedFiltered = dayCases.filter(
      (case_) => case_.cartCompletenessStatus === 'Trays Unavailable'
    );

    return blockedFiltered.map((case_) => {
      const timeUntilSurgery = CaseScheduleRepository.calculateTimeUntilSurgery(
        case_.dateTime
      );
      const isPriority = timeUntilSurgery.includes('59mins'); // A bit of a hack, but matches original logic

      return new WorkflowCaseItemEntity({
        id: case_.id,
        caseHospitalId: case_.caseHospitalId || case_.id, // Fallback to ID if hospital ID is missing
        surgeryTime: CaseScheduleRepository.formatSurgeryTime(case_.dateTime),
        dueTime: timeUntilSurgery,
        action:
          case_.cartCompletenessStatus === 'Ready for Dispatch'
            ? 'Dispatch'
            : 'Build',
        type: CaseScheduleRepository.getCaseType(
          case_.cartCompletenessStatus as CartCompletenessStatus
        ),
        isPriority,
      });
    });
  }

  private getInProgressSummary(dayCases: SurgicalCaseEntity[]): {
    total: number;
    user?: { initials: string; name: string };
  } {
    // Stub implementation - needs to be completed
    return { total: 0 };
  }

  private getDispatchedSummary(dayCases: SurgicalCaseEntity[]): {
    total: number;
    complete: number;
    incomplete: number;
  } {
    // Stub implementation - needs to be completed
    const dispatched = dayCases.filter(
      (case_) =>
        case_.cartCompletenessStatus === 'Dispatched' ||
        case_.cartCompletenessStatus === 'Dispatched - Incomplete'
    );
    const complete = dispatched.filter(
      (case_) => case_.cartCompletenessStatus === 'Dispatched'
    ).length;
    return {
      total: dispatched.length,
      complete,
      incomplete: dispatched.length - complete,
    };
  }

  // --- General Formatting & Utility Helpers ---

  /**
   * Convert a date-only string (YYYY-MM-DD) to ISO string at start of day (00:00:00) in local timezone
   * If the string is already an ISO datetime string, extracts the date part and converts to start of day
   */
  private static convertDateToStartOfDayISO(dateString: string): string {
    // Extract date part if it's an ISO string (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss...)
    const datePart = dateString.split('T')[0];
    // Parse the date string as local time (not UTC) to avoid timezone shifts
    const [year, month, day] = datePart.split('-').map(Number);
    const date = new Date(year, month - 1, day, 0, 0, 0, 0);
    return date.toISOString();
  }

  /**
   * Convert a date-only string (YYYY-MM-DD) to ISO string at end of day (23:59:59.999) in local timezone
   * If the string is already an ISO datetime string, extracts the date part and converts to end of day
   */
  private static convertDateToEndOfDayISO(dateString: string): string {
    // Extract date part if it's an ISO string (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss...)
    const datePart = dateString.split('T')[0];
    // Parse the date string as local time (not UTC) to avoid timezone shifts
    const [year, month, day] = datePart.split('-').map(Number);
    const date = new Date(year, month - 1, day, 23, 59, 59, 999);
    return date.toISOString();
  }

  /**
   * Calculate time until surgery
   */
  private static calculateTimeUntilSurgery(surgeryTime: Date): string {
    const now = new Date();
    const diffMs = surgeryTime.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffMs < 0) {
      return 'Overdue';
    }

    if (diffHours > 0) {
      return `${diffHours}hrs`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}mins`;
    } else {
      // Priority case, per original logic
      return '59mins';
    }
  }

  /**
   * Format surgery time for display
   */
  private static formatSurgeryTime(surgeryTime: Date): string {
    return surgeryTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  /**
   * Format date label for display
   */
  private static formatDateLabel(date: Date): string {
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${day}${this.getOrdinalSuffix(day)} ${month}`;
  }

  /**
   * Get ordinal suffix for day
   */
  private static getOrdinalSuffix(day: number): string {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  /**
   * Get case type based on cart completeness status
   */
  private static getCaseType(
    status: CartCompletenessStatus
  ): 'Ready for Dispatch' | 'Build Incomplete' | 'Build Not Started' {
    switch (status) {
      case 'Ready for Dispatch':
        return 'Ready for Dispatch';
      case 'Incomplete':
        return 'Build Incomplete';
      case 'Not Started':
        return 'Build Not Started';
      default:
        // Default fall-through for any other statuses
        return 'Build Not Started';
    }
  }
}

// Export singleton
export const caseScheduleRepository = new CaseScheduleRepository(
  caseScheduleApiClient
);

// Export alias for backward compatibility
export const mockCaseScheduleRepository = caseScheduleRepository;
// Export class alias if needed
export const MockCaseScheduleRepository = CaseScheduleRepository;
